<template>
  <div>
    <!-- ⚠️ อ่านอย่างเดียวโดยตั้งใจ — สารบัญนี้เป็นตัวเดียวกับที่ QaCaseClassifier ใช้ติด
         หัวข้อให้เคสจริง และแก้ที่ sy.Dbsettings เท่านั้น · ถ้ามีปุ่มบันทึกที่นี่แล้วมันไม่ได้
         เขียนลงที่เดียวกัน คนจะแก้หัวข้อแล้วงงว่าทำไมเคสใหม่ยังถูกจัดแบบเดิม -->
    <v-alert type="info" variant="tonal" density="compact" class="mb-3" icon="mdi-information-outline">
      <span class="text-body-2">
        {{ $t('qaTaxSourceNote') }}
      </span>
    </v-alert>

    <!-- ไม่มีค่าใน Dbsettings → ระบบใช้สารบัญ built-in ต้องบอก ไม่งั้นคนจะคิดว่าค่าที่ตัวเอง
         ใส่ไว้ถูกอ่านแล้ว ทั้งที่ระบบตกกลับไปใช้ค่าตั้งต้นเพราะ parse ไม่ผ่าน/ไม่มีคีย์ -->
    <v-alert v-if="isDefault" type="warning" variant="tonal" density="compact" class="mb-3" icon="mdi-alert-outline">
      <span class="text-body-2">
        {{ $t('qaTaxDefaultNote') }}
      </span>
    </v-alert>

    <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-3">
      <span class="text-body-2">{{ $t('qaTaxLoadFailed', { msg: error.message || error }) }}</span>
    </v-alert>

    <v-card class="qa-card-sm mb-3" variant="outlined">
      <v-card-title class="d-flex align-center py-3">
        <v-icon size="18" color="primary" class="mr-2">mdi-shape-outline</v-icon>
        <span class="text-subtitle-1">{{ $t('qaTaxTitle') }}</span>
        <v-spacer />
        <v-chip size="small" variant="tonal" label>{{ $t('qaTaxCount', { topics: topics.length, categories: categories.length }) }}</v-chip>
      </v-card-title>
    </v-card>

    <v-skeleton-loader v-if="loading" type="card" />

    <template v-else>
      <v-card v-for="cat in categories" :key="cat.categoryId" class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="d-flex align-center py-3">
          <v-chip size="small" label variant="tonal" color="primary">{{ cat.categoryId }}</v-chip>
          <v-spacer />
          <span class="text-caption text-medium-emphasis">{{ $t('qaTaxTopicsIn', { n: cat.topics.length }) }}</span>
        </v-card-title>
        <v-divider />
        <v-table density="comfortable">
          <thead>
            <tr>
              <th style="width: 240px">topicId</th>
              <th>{{ $t('qaTaxColName') }}</th>
            </tr>
          </thead>
          <tbody>
            <!-- ⚠️ ต้องโชว์ topicId ดิบ ไม่ใช่แค่ชื่อ — เวลากรองหน้าอื่นไม่ติด สิ่งที่ต้องเทียบ
                 คือค่านี้กับ TopicId ในตาราง QaCases ไม่ใช่ชื่อที่คนอ่าน -->
            <tr v-for="topic in cat.topics" :key="topic.topicId">
              <td class="text-body-2 qa-num text-medium-emphasis">{{ topic.topicId }}</td>
              <td class="text-body-2">{{ topic.label }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card>

      <v-card v-if="!categories.length && !error" class="qa-card-sm" variant="outlined">
        <div class="text-center text-medium-emphasis py-8 text-body-2">{{ $t('qaTaxEmpty') }}</div>
      </v-card>
    </template>
  </div>
</template>

<script>
/**
 * แท็บ "หัวข้อสาย" — อ่านสารบัญหัวข้อของจริงจาก GET topics
 *
 * ⚠️ ส่วน "ทีม / หัวหน้าทีม / วิธีจัดหมวด" ถูกตัดทิ้งทั้งก้อนโดยตั้งใจ — เป็นชื่อคนและ
 *    โครงองค์กรที่แต่งขึ้นมาทั้งหมด ไม่เคยผูกกับข้อมูลจริงหรือกับพฤติกรรมของระบบเลย
 *    (สวิตช์ "IVR code / LLM" ก็ไม่เคยถูกอ่านที่ไหน — ตัวจัดหมวดจริงใช้ LLM อย่างเดียว)
 *    ค่าตั้งปลอมที่ดูเหมือนสั่งงานได้ อันตรายกว่าการไม่มีช่องให้ตั้งเลย
 */
import qaConsole from '@/services/qaConsole';

export default {
  name: 'QaTaxonomySettings',
  // ยังประกาศไว้เพราะหน้า QASettings ผูก @saved ไว้กับทุกแท็บ (แท็บนี้อ่านอย่างเดียว
  // จึงไม่มีวัน emit) — ถ้าไม่ประกาศ listener จะหล่นไปเป็น attribute บน root element
  emits: ['saved'],
  data() {
    return { topics: [], isDefault: false, loading: true, error: null };
  },
  computed: {
    /** จัดกลุ่มตาม categoryId — สารบัญจริงมีแค่ id ของหมวด ไม่มีชื่อหมวดแยกมาให้ */
    categories() {
      const byId = new Map();
      this.topics.forEach((t) => {
        const key = t.categoryId || this.$t('qaTaxNoCategory');
        if (!byId.has(key)) byId.set(key, { categoryId: key, topics: [] });
        byId.get(key).topics.push(t);
      });
      return [...byId.values()];
    },
  },
  async mounted() {
    try {
      const res = await qaConsole.getTopics();
      this.topics = res.items || [];
      this.isDefault = !!res.isDefault;
    } catch (err) {
      this.error = err;
    } finally {
      this.loading = false;
    }
  },
};
</script>
